/**
 * locale-registry.ts
 *
 * Central Locale Registry managing active BCP 47 locale, LTR/RTL text direction,
 * DOM html attributes, dynamic namespace and LanguageBundle registration, and reactive subscriptions.
 */

import { type Bcp47LocaleTag, type TextDirection, SUPPORTED_LOCALES, type LocalePreference } from './locale-contracts.js';
import { detectBrowserLocale, normalizeBcp47Locale } from './locale-detector.js';
import { globalTranslationService } from './translation-service.js';
import type { LanguageBundle } from '../plugins/sdk/i18n-contracts.js';

export class LocaleRegistry {
  private currentLocale: Bcp47LocaleTag = 'en';
  private listeners = new Set<(locale: Bcp47LocaleTag, direction: TextDirection) => void>();

  constructor() {
    this.currentLocale = this.getInitialLocale();
    this.applyDomAttributes(this.currentLocale);

    // Forward translation service updates to locale subscribers
    globalTranslationService.subscribe(() => {
      const dir = this.getDirection();
      this.listeners.forEach((fn) => fn(this.currentLocale, dir));
    });
  }

  getLocale(): Bcp47LocaleTag {
    return this.currentLocale;
  }

  getDirection(): TextDirection {
    return (SUPPORTED_LOCALES as any)[this.currentLocale]?.direction || 'ltr';
  }

  setLocale(tag: string, source: LocalePreference['source'] = 'user'): void {
    const normalized = normalizeBcp47Locale(tag);
    if (this.currentLocale === normalized) return;

    this.currentLocale = normalized;
    this.applyDomAttributes(normalized);

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('mesh_locale_preference', JSON.stringify({
          locale: normalized,
          source,
          updatedAt: new Date().toISOString(),
        }));
      }
    } catch {
      // Ignore storage errors
    }

    // Broadcast to React listeners
    const dir = this.getDirection();
    this.listeners.forEach((fn) => fn(normalized, dir));

    // Keep LanguageRegistryService in sync
    try {
      import('./language-registry-service.js').then(({ LanguageRegistryService }) => {
        const langService = LanguageRegistryService.getInstance();
        const current = langService.getUserLocaleSettings();
        if (current.language !== normalized && normalizeBcp47Locale(current.language) !== normalized) {
          langService.setUserLanguage(normalized, false);
        }
      }).catch(() => {});
    } catch {
      // Ignore
    }

    // Broadcast to iframes
    try {
      if (typeof document !== 'undefined' && typeof document.querySelectorAll === 'function') {
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach((iframe) => {
          iframe.contentWindow?.postMessage(
            {
              type: 'LOCALE_CHANGED',
              protocolVersion: 'i18n/v1',
              locale: normalized,
              direction: dir,
            },
            '*'
          );
        });
      }
    } catch (err) {
      console.warn('[LocaleRegistry] Failed to broadcast locale update to micro-app iframes:', err);
    }
  }

  /**
   * Registers a dynamic namespace catalog for a specific language.
   */
  registerNamespace(lang: string, namespace: string, catalog: Record<string, string>): void {
    globalTranslationService.registerNamespace(lang, namespace, catalog);
    const dir = this.getDirection();
    this.listeners.forEach((fn) => fn(this.currentLocale, dir));
  }

  /**
   * Registers or updates a full LanguageBundle.
   */
  registerBundle(bundle: LanguageBundle): void {
    globalTranslationService.registerBundle(bundle);
    const dir = this.getDirection();
    this.listeners.forEach((fn) => fn(this.currentLocale, dir));
  }

  /**
   * Exports an active language bundle.
   */
  exportBundle(locale?: string): LanguageBundle {
    return globalTranslationService.exportLanguageBundle(locale || this.currentLocale);
  }

  /**
   * Retrieves active namespaces for a locale.
   */
  getNamespaces(locale?: string): string[] {
    return globalTranslationService.getRegisteredNamespaces(locale || this.currentLocale);
  }

  subscribe(listener: (locale: Bcp47LocaleTag, direction: TextDirection) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private applyDomAttributes(locale: Bcp47LocaleTag): void {
    if (typeof document !== 'undefined' && document.documentElement) {
      const dir = (SUPPORTED_LOCALES as any)[locale]?.direction || 'ltr';
      document.documentElement.lang = locale;
      document.documentElement.dir = dir;
    }
  }

  private getInitialLocale(): Bcp47LocaleTag {
    try {
      if (typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem('mesh_locale_preference');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && parsed.locale && (SUPPORTED_LOCALES as any)[parsed.locale as Bcp47LocaleTag]) {
            return parsed.locale;
          }
        }
      }
    } catch {
      // Fall through to browser detector
    }
    return detectBrowserLocale();
  }
}

export const globalLocaleRegistry = new LocaleRegistry();
